import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ArrowRight } from "lucide-react";
import { IMG } from "../lib/images";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import EventCard, { CATEGORIES } from "../components/EventCard";
import { Reveal } from "../components/motion";
import NextGathering from "../components/NextGathering";

export default function Agenda() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? events : events.filter((e) => e.category === filter)),
    [events, filter]
  );

  return (
    <>
      <PageHeader
        testid="agenda-header"
        eyebrow="Agenda"
        titleLines={["Nos rencontres", "et événements à venir"]}
        intro="Charity Times, temps de prière, évangélisation, moments fraternels : retrouvez ici toutes les dates de la vie de l'église."
        image={IMG.priere}
      />
      <section className="py-16 sm:py-24">
        <div className="container-site">
          <div className="grid gap-10 lg:grid-cols-[1fr_340px]">
            <div>
              <div data-testid="agenda-filters" className="flex flex-wrap gap-2">
                <button
                  data-testid="agenda-filter-all"
                  onClick={() => setFilter("all")}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${filter === "all" ? "bg-obsidian text-white" : "border border-line bg-white text-stone-600 hover:border-terracotta hover:text-terracotta"}`}
                >
                  Tout
                </button>
                {Object.entries(CATEGORIES).map(([key, c]) => (
                  <button
                    key={key}
                    data-testid={`agenda-filter-${key}`}
                    onClick={() => setFilter(key)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${filter === key ? "bg-obsidian text-white" : "border border-line bg-white text-stone-600 hover:border-terracotta hover:text-terracotta"}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="mt-8 space-y-4">
                {loading && <p className="text-sm text-stone-500">Chargement des événements…</p>}
                {!loading && !filtered.length && (
                  <div className="card-warm border-dashed p-10 text-center">
                    <CalendarDays className="mx-auto h-6 w-6 text-terracotta" />
                    <p className="mt-4 text-sm text-stone-500">Aucun événement dans cette catégorie pour le moment.</p>
                  </div>
                )}
                {filtered.map((ev, i) => (
                  <Reveal key={ev.id} delay={Math.min(i * 0.05, 0.3)}>
                    <EventCard ev={ev} />
                  </Reveal>
                ))}
              </div>
            </div>
            <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
              <NextGathering />
              <Reveal>
                <div className="card-warm overflow-hidden">
                  <img
                    data-testid="agenda-calendar-flyer"
                    src="/photos/calendrier-rentree-2026.png"
                    alt="Calendrier des rencontres Charity Times — rentrée automne 2026"
                    className="w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </Reveal>
              <Reveal>
                <div className="card-warm overflow-hidden">
                  <img
                    data-testid="agenda-mercredi-flyer"
                    src="/photos/mercredi-priere-flyer.jpg"
                    alt="Mercredi en prière — jeûne et prière chaque mercredi, réunion en ligne 19h à 20h15"
                    className="w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </Reveal>
              <Reveal>
                <div className="card-warm p-6">
                  <p className="label-eyebrow">Première fois ?</p>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600">
                    Venez comme vous êtes : aucune inscription, aucune tenue particulière.
                  </p>
                  <Link data-testid="agenda-cta-first-visit" to="/premiere-visite" className="btn-primary mt-5 w-full">
                    Préparer ma visite
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </Reveal>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
